import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useOutletContext } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth, roleHome } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

import MarketingLayout from "@/components/marketing/MarketingLayout";
import ScrollToTop from "@/components/marketing/ScrollToTop";
import Canonical from "@/components/marketing/Canonical";
import RouteSeo from "@/components/marketing/RouteSeo";
import Home from "@/pages/Home";
import Solutions from "@/pages/Solutions";
import Services from "@/pages/Services";
import BenchmarkReport from "@/pages/BenchmarkReport";
import Sectors from "@/pages/Sectors";
import SectorDetail from "@/pages/SectorDetail";
import AISystems from "@/pages/AISystems";
import Enterprise from "@/pages/Enterprise";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import BookDemo from "@/pages/BookDemo";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import DemoAccess from "@/pages/DemoAccess";
import Settings from "@/pages/dashboard/Settings";
import StandaloneSettings from "@/pages/dashboard/StandaloneSettings";
import Replay from "@/pages/Replay";
import Status from "@/pages/Status";
import Docs from "@/pages/Docs";
import Customers from "@/pages/Customers";
import Changelog from "@/pages/Changelog";
import Roadmap from "@/pages/Roadmap";
import Start from "@/pages/Start";
import Careers from "@/pages/Careers";
import Activity from "@/pages/Activity";
import Leaderboard from "@/pages/Leaderboard";
import PublishersLeaderboard from "@/pages/PublishersLeaderboard";
import Partners from "@/pages/Partners";
import PartnersApply from "@/pages/PartnersApply";
import BuyLeads from "@/pages/BuyLeads";
import BuyLeadsOrderSuccess from "@/pages/BuyLeadsOrderSuccess";
import ReserveSuccess from "@/pages/ReserveSuccess";
import InsideConnexis from "@/pages/InsideConnexis";
import Integrations from "@/pages/Integrations";
import FAQ from "@/pages/FAQ";
import PayPerCall from "@/pages/PayPerCall";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import CommandPalette from "@/components/CommandPalette";
import CookieConsent from "@/components/CookieConsent";

// Legal / Trust
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import TermsBuyer from "@/pages/legal/TermsBuyer";
import TermsPublisher from "@/pages/legal/TermsPublisher";
import TermsAffiliate from "@/pages/legal/TermsAffiliate";
import DPA from "@/pages/legal/DPA";
import AcceptableUse from "@/pages/legal/AcceptableUse";
import TrustCenter from "@/pages/TrustCenter";

// Contracts (e-signature vault)
import BuyerContracts from "@/pages/dashboard/Contracts";
import PublisherContracts from "@/pages/publisher/Contracts";
import AffiliateContracts from "@/pages/affiliate/Contracts";
import AdminContracts from "@/pages/admin/Contracts";

import DashboardLayout from "@/components/dashboard/DashboardLayout";
import ClientOverview from "@/pages/dashboard/Overview";
import ClientLeads from "@/pages/dashboard/Leads";
import ClientOrders from "@/pages/dashboard/Orders";
import ClientDelivery from "@/pages/dashboard/Delivery";
import ClientCompliance from "@/pages/dashboard/Compliance";
import ClientAI from "@/pages/dashboard/AISystems";
import LiveCallsDashboard from "@/pages/dashboard/LiveCalls";
import AutoFollowUp from "@/pages/dashboard/AutoFollowUp";
import Replies from "@/pages/dashboard/Replies";
import AddonsCatalogue from "@/pages/dashboard/AddonsCatalogue";
import ClientCampaign from "@/pages/dashboard/Campaign";
import ClientBilling from "@/pages/dashboard/Billing";
import ClientSupport from "@/pages/dashboard/Support";
import MyFeedback from "@/pages/dashboard/MyFeedback";
import EmbedBuyerReport from "@/pages/EmbedBuyerReport";
import EmbedSectorStats from "@/pages/EmbedSectorStats";

import ChooseArea from "@/pages/ChooseArea";
import AdminOverview from "@/pages/admin/Overview";
import AdminRouting from "@/pages/admin/Routing";
import AdminBuyers from "@/pages/admin/Buyers";
import AdminCampaigns from "@/pages/admin/Campaigns";
import AdminCareers from "@/pages/admin/Careers";
import AdminReplay from "@/pages/admin/Replay";
import AdminCompliance from "@/pages/admin/Compliance";
import AdminComplianceCenter from "@/pages/admin/ComplianceCenter";
import AdminMarketplaceModeration from "@/pages/admin/MarketplaceModeration";
import DsarRequest from "@/pages/legal/DsarRequest";
import AdminUsers from "@/pages/admin/Users";
import AdminFeedback from "@/pages/admin/Feedback";
import AdminEnquiries from "@/pages/admin/Enquiries";
import AdminCleanup from "@/pages/admin/Cleanup";
import AdminPartners from "@/pages/admin/Partners";
import AdminAudit from "@/pages/admin/Audit";
import AdminSecurity from "@/pages/admin/Security";
import AdminInvoices from "@/pages/admin/Invoices";
import AdminPublishers from "@/pages/admin/Publishers";
import AdminSupport from "@/pages/admin/Support";
import AdminVerifyTransfer from "@/pages/admin/VerifyTransfer";
import AdminSectorDemand from "@/pages/admin/SectorDemand";
import AdminContent from "@/pages/admin/Content";
import AdminContentEditor from "@/pages/admin/ContentEditor";

// Connexis Hub — private lead pingtree (admin) + lender + referrer portals
import HubLayout from "@/pages/hub/HubLayout";
import HubOverview from "@/pages/hub/Overview";
import HubDesk from "@/pages/hub/Desk";
import HubPipeline from "@/pages/hub/Pipeline";
import HubLeads from "@/pages/hub/Leads";
import HubLeadDetail from "@/pages/hub/LeadDetail";
import HubLenders from "@/pages/hub/Lenders";
import HubReferrers from "@/pages/hub/Referrers";
import HubRules from "@/pages/hub/Rules";
import LenderPortal from "@/pages/lender/Portal";
import LenderCases from "@/pages/lender/Cases";
import LenderCaseRecord from "@/pages/lender/CaseRecord";
import LenderCriteria from "@/pages/lender/Criteria";
import LenderCrm from "@/pages/lender/Crm";
import LenderFees from "@/pages/lender/Fees";
import LenderDocuments from "@/pages/lender/Documents";
import LenderSettings from "@/pages/lender/Settings";
import IntroducerPortal from "@/pages/introducer/Portal";
import IntroducerWorkspace from "@/pages/introducer/Workspace";
import IntroducerCampaignHome from "@/pages/introducer/CampaignHome";
import IntroducerFloorHome from "@/pages/introducer/FloorHome";
import IntroducerBoard from "@/pages/introducer/Board";
import IntroducerChannels from "@/pages/introducer/Channels";
import IntroducerApi from "@/pages/introducer/ApiAccess";
import CampaignRedirect from "@/pages/CampaignRedirect";
import IntroducerSubmit from "@/pages/introducer/SubmitCase";
import IntroducerCaseRecord from "@/pages/introducer/CaseRecord";
import IntroducerStatements from "@/pages/introducer/Statements";
import IntroducerPerformance from "@/pages/introducer/Performance";
import IntroducerMediaKit from "@/pages/introducer/MediaKit";
import IntroducerNetwork from "@/pages/introducer/Network";
import IntroducerPaperwork from "@/pages/introducer/Paperwork";
import IntroducerAccount from "@/pages/introducer/Account";
import PortalMessages from "@/pages/portal/Messages";
import AskConnie from "@/pages/portal/AskConnie";
import HelpShape from "@/pages/portal/HelpShape";
import HubConnie from "@/pages/hub/Connie";
import HubFeedback from "@/pages/hub/Feedback";
import HubWithdrawn from "@/pages/hub/Withdrawn";
import HubPartnerPhones from "@/pages/hub/PartnerPhones";
import HubInvite from "@/pages/HubInvite";
import PublicApply from "@/pages/PublicApply";
import HubApplications from "@/pages/hub/Applications";
import HubCustomerApps from "@/pages/hub/CustomerApps";
import HubEnquiries from "@/pages/hub/Enquiries";
import ApplyStatus from "@/pages/ApplyStatus";
import ClientDocUpload from "@/pages/ClientDocUpload";
import ClientAnswer from "@/pages/ClientAnswer";
import ClientCaseChase from "@/pages/ClientCaseChase";
import LendingNetwork from "@/pages/LendingNetwork";
import PartnerWithUs from "@/pages/PartnerWithUs";
import BusinessFunding from "@/pages/BusinessFunding";
import HubMessages from "@/pages/hub/Messages";
import HubAgreements from "@/pages/hub/Agreements";
import DeskNotifications from "@/pages/hub/DeskNotifications";
import HubBin from "@/pages/hub/Bin";
import HubSiteEffects from "@/pages/hub/SiteEffects";
import PortalSignIn from "@/pages/portal/PortalSignIn";
import PortalApply from "@/pages/portal/PortalApply";
import PortalLaunch from "@/pages/PortalLaunch";

import AffiliateOverview from "@/pages/affiliate/Overview";
import AffiliateLinks from "@/pages/affiliate/Links";
import AffiliateCommissions from "@/pages/affiliate/Commissions";
import AffiliateCreatives from "@/pages/affiliate/Creatives";

import PartnerProfile from "@/pages/PartnerProfile";
import EmbedPingTree from "@/pages/EmbedPingTree";
import WhyConnexis from "@/pages/WhyConnexis";
import PublisherApply from "@/pages/PublisherApply";
import QrRedeem from "@/pages/QrRedeem";
import PublisherLayout from "@/components/publisher/PublisherLayout";
import PublisherOverview from "@/pages/publisher/Overview";
import PublisherLeads from "@/pages/publisher/Leads";
import PublisherSectors from "@/pages/publisher/Sectors";
import PublisherEarnings from "@/pages/publisher/Earnings";
import PublisherPayouts from "@/pages/publisher/Payouts";
import PublisherApiKeys from "@/pages/publisher/ApiKeys";
import PublisherLeaderboard from "@/pages/publisher/Leaderboard";
import PublisherSupport from "@/pages/publisher/Support";
import PublisherLanders from "@/pages/publisher/Landers";
import PublisherLanderEdit from "@/pages/publisher/LanderEdit";
import PublicLander from "@/pages/public/PublicLander";
import AffiliateAnalytics from "@/pages/affiliate/Analytics";
import AffiliateLanders from "@/pages/affiliate/Landers";
import AiOrderSuccess from "@/pages/AiOrderSuccess";

function Wrap({ children }) { return <MarketingLayout>{children}</MarketingLayout>; }

/* A campaign partner (media / affiliate) lands on links + stats; a call centre on its floor;
   an introducer who works cases themselves on those cases. */
function IntroducerHome() {
  const { campaign, floor } = useOutletContext();
  if (campaign) return <IntroducerCampaignHome />;
  if (floor) return <IntroducerFloorHome />;
  return <IntroducerWorkspace />;
}

function PostLoginRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={roleHome(user.role)} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Canonical />
        <RouteSeo />
        <CommandPalette />
        <Routes>
          {/* Marketing */}
          <Route path="/" element={<Wrap><Home /></Wrap>} />
          <Route path="/solutions" element={<Wrap><Solutions /></Wrap>} />
          <Route path="/services" element={<Wrap><Services /></Wrap>} />
          <Route path="/benchmark-report" element={<Wrap><BenchmarkReport /></Wrap>} />
          <Route path="/sectors" element={<Wrap><Sectors /></Wrap>} />
          <Route path="/sectors/:slug" element={<Wrap><SectorDetail /></Wrap>} />
          <Route path="/ai-systems" element={<Wrap><AISystems /></Wrap>} />
          <Route path="/enterprise" element={<Wrap><Enterprise /></Wrap>} />
          <Route path="/pricing" element={<Wrap><Pricing /></Wrap>} />
          <Route path="/about" element={<Wrap><About /></Wrap>} />
          <Route path="/contact" element={<Wrap><Contact /></Wrap>} />
          <Route path="/book-demo" element={<Wrap><BookDemo /></Wrap>} />
          <Route path="/status" element={<Status />} />
          <Route path="/docs" element={<Docs />} />
          {/* Iter97: embeddable buyer report. No chrome — token-gated public route. */}
          <Route path="/embed/buyer-report" element={<EmbedBuyerReport />} />
          {/* Iter98: public sector-stats trust widget. No chrome, no auth, anonymous. */}
          <Route path="/embed/sector-stats" element={<EmbedSectorStats />} />
          <Route path="/customers" element={<Wrap><Customers /></Wrap>} />
          <Route path="/changelog" element={<Wrap><Changelog /></Wrap>} />
          <Route path="/roadmap" element={<Wrap><Roadmap /></Wrap>} />
          <Route path="/start" element={<Wrap><Start /></Wrap>} />
          <Route path="/careers" element={<Wrap><Careers /></Wrap>} />
          <Route path="/careers/:slug" element={<Wrap><Careers /></Wrap>} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/leaderboard" element={<Wrap><Leaderboard /></Wrap>} />
          <Route path="/publishers/leaderboard" element={<Wrap><PublishersLeaderboard /></Wrap>} />
          <Route path="/partners" element={<Wrap><Partners /></Wrap>} />
          <Route path="/partners/apply" element={<Wrap><PartnersApply /></Wrap>} />
          <Route path="/why-connexis" element={<Wrap><WhyConnexis /></Wrap>} />
          <Route path="/lending-network" element={<Wrap><LendingNetwork /></Wrap>} />
          <Route path="/partner-with-us" element={<Wrap><PartnerWithUs /></Wrap>} />
          <Route path="/business-funding" element={<Wrap><BusinessFunding /></Wrap>} />
          <Route path="/business-funding/:slug" element={<Wrap><BusinessFunding /></Wrap>} />
          <Route path="/property-finance" element={<Wrap><BusinessFunding sector="property" /></Wrap>} />
          <Route path="/property-finance/:slug" element={<Wrap><BusinessFunding sector="property" /></Wrap>} />
          <Route path="/healthcare-finance" element={<Wrap><BusinessFunding sector="healthcare" /></Wrap>} />
          <Route path="/healthcare-finance/:slug" element={<Wrap><BusinessFunding sector="healthcare" /></Wrap>} />
          <Route path="/manufacturing-finance" element={<Wrap><BusinessFunding sector="manufacturing" /></Wrap>} />
          <Route path="/manufacturing-finance/:slug" element={<Wrap><BusinessFunding sector="manufacturing" /></Wrap>} />
          <Route path="/hospitality-finance" element={<Wrap><BusinessFunding sector="hospitality" /></Wrap>} />
          <Route path="/hospitality-finance/:slug" element={<Wrap><BusinessFunding sector="hospitality" /></Wrap>} />
          <Route path="/introducers" element={<Navigate to="/lending-network" replace />} />
          <Route path="/lenders" element={<Navigate to="/lending-network" replace />} />
          <Route path="/inside-connexis" element={<Wrap><InsideConnexis /></Wrap>} />
          <Route path="/inside" element={<Navigate to="/inside-connexis" replace />} />
          <Route path="/how-it-works" element={<Navigate to="/inside-connexis" replace />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/faqs" element={<Navigate to="/faq" replace />} />
          <Route path="/pay-per-call" element={<PayPerCall />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightDetail />} />
          <Route path="/calls" element={<Navigate to="/pay-per-call" replace />} />
          <Route path="/live-calls" element={<Navigate to="/pay-per-call" replace />} />
          <Route path="/inbound-calls" element={<Navigate to="/pay-per-call" replace />} />
          <Route path="/publisher/apply" element={<Wrap><PublisherApply /></Wrap>} />
          <Route path="/sell-leads" element={<Navigate to="/publisher/apply" replace />} />
          <Route path="/sell" element={<Navigate to="/publisher/apply" replace />} />
          {/* iter83 — QR phone handoff redeem route. Visited when an operator
              scans the QR generated by the dashboard "View on phone" button. */}
          <Route path="/m/:token" element={<QrRedeem />} />
          <Route path="/buy-leads" element={<Wrap><BuyLeads /></Wrap>} />
          <Route path="/buy-leads/order-success" element={<Wrap><BuyLeadsOrderSuccess /></Wrap>} />
          <Route path="/buy" element={<Navigate to="/buy-leads" replace />} />
          <Route path="/buyers" element={<Navigate to="/buy-leads" replace />} />
          <Route path="/ai-systems/order-success" element={<Wrap><AiOrderSuccess /></Wrap>} />
          <Route path="/replay/:sid" element={<Replay />} />
          <Route path="/reserve-success" element={<Wrap><ReserveSuccess /></Wrap>} />
          <Route path="/p/:alias" element={<Wrap><PartnerProfile /></Wrap>} />
          <Route path="/embed/ping-tree" element={<EmbedPingTree />} />

          {/* Legal & Trust */}
          <Route path="/legal/privacy"          element={<Wrap><PrivacyPolicy /></Wrap>} />
          <Route path="/legal/cookies"          element={<Wrap><CookiePolicy /></Wrap>} />
          <Route path="/legal/terms-buyer"      element={<Wrap><TermsBuyer /></Wrap>} />
          <Route path="/legal/terms-publisher"  element={<Wrap><TermsPublisher /></Wrap>} />
          <Route path="/legal/terms-affiliate"  element={<Wrap><TermsAffiliate /></Wrap>} />
          <Route path="/legal/dpa"              element={<Wrap><DPA /></Wrap>} />
          <Route path="/legal/acceptable-use"   element={<Wrap><AcceptableUse /></Wrap>} />
          <Route path="/legal/dsar"             element={<DsarRequest />} />
          <Route path="/legal"                  element={<Navigate to="/legal/privacy" replace />} />
          <Route path="/trust"                  element={<Wrap><TrustCenter /></Wrap>} />
          <Route path="/privacy"                element={<Navigate to="/legal/privacy" replace />} />
          <Route path="/terms"                  element={<Navigate to="/legal/terms-buyer" replace />} />

          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/app" element={<PostLoginRedirect />} />
          <Route path="/demo-access" element={<DemoAccess />} />
          <Route path="/demo" element={<DemoAccess />} />
          <Route path="/demo/:portal" element={<DemoAccess />} />
          <Route path="/choose" element={<ProtectedRoute roles={["admin"]}><ChooseArea /></ProtectedRoute>} />

          {/* Client */}
          <Route path="/dashboard" element={<ProtectedRoute roles={["client", "admin"]}><DashboardLayout role="client" /></ProtectedRoute>}>
            <Route index element={<ClientOverview />} />
            <Route path="orders" element={<ClientOrders />} />
            <Route path="leads" element={<ClientLeads />} />
            <Route path="delivery" element={<ClientDelivery />} />
            <Route path="compliance" element={<ClientCompliance />} />
            <Route path="ai-systems" element={<ClientAI />} />
            <Route path="live-calls" element={<LiveCallsDashboard />} />
            <Route path="auto-followup" element={<AutoFollowUp />} />
            <Route path="replies" element={<Replies />} />
            <Route path="addons" element={<AddonsCatalogue />} />
            <Route path="campaign" element={<ClientCampaign />} />
            <Route path="billing" element={<ClientBilling />} />
            <Route path="contracts" element={<BuyerContracts />} />
            <Route path="support" element={<ClientSupport />} />
            <Route path="my-feedback" element={<MyFeedback />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Admin one-click verify-transfer (iter88) — standalone full-screen
              so it works from a phone SMS tap without sidebar chrome. */}
          <Route path="/admin/v/:orderId" element={<AdminVerifyTransfer />} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><DashboardLayout role="admin" /></ProtectedRoute>}>
            <Route index element={<AdminOverview />} />
            <Route path="routing" element={<AdminRouting />} />
            <Route path="buyers" element={<AdminBuyers />} />
            <Route path="campaigns" element={<AdminCampaigns />} />
            <Route path="compliance" element={<AdminCompliance />} />
            <Route path="compliance-center" element={<AdminComplianceCenter />} />
            <Route path="marketplace" element={<AdminMarketplaceModeration />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="enquiries" element={<AdminEnquiries />} />
            <Route path="cleanup" element={<AdminCleanup />} />
            <Route path="careers" element={<AdminCareers />} />
            <Route path="replay" element={<AdminReplay />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="publishers" element={<AdminPublishers />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="contracts" element={<AdminContracts />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="security" element={<AdminSecurity />} />
            <Route path="sector-demand" element={<AdminSectorDemand />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="content/new" element={<AdminContentEditor />} />
            <Route path="content/:id" element={<AdminContentEditor />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Connexis Hub — private admin lead pingtree */}
          <Route path="/hub" element={<ProtectedRoute roles={["admin"]}><HubLayout /></ProtectedRoute>}>
            <Route index element={<HubDesk />} />
            <Route path="desk-tools" element={<HubOverview />} />
            <Route path="pipeline" element={<HubPipeline />} />
            <Route path="leads" element={<HubLeads />} />
            <Route path="leads/:id" element={<HubLeadDetail />} />
            <Route path="lenders" element={<HubLenders />} />
            <Route path="referrers" element={<HubReferrers />} />
            <Route path="rules" element={<HubRules />} />
            <Route path="applications" element={<HubApplications />} />
            <Route path="customer-apps" element={<HubCustomerApps />} />
            <Route path="enquiries" element={<HubEnquiries />} />
            <Route path="messages" element={<HubMessages />} />
            <Route path="connie" element={<HubConnie />} />
            <Route path="feedback" element={<HubFeedback />} />
            <Route path="withdrawn" element={<HubWithdrawn />} />
            <Route path="partner-phones" element={<HubPartnerPhones />} />
            <Route path="agreements" element={<HubAgreements />} />
            <Route path="alerts-settings" element={<DeskNotifications />} />
            <Route path="bin" element={<HubBin />} />
            <Route path="site-effects" element={<HubSiteEffects />} />
          </Route>

          {/* Public premium sign-in + apply flows for lenders/introducers */}
          <Route path="/lender/login"      element={<PortalSignIn kind="lender" />} />
          <Route path="/introducer/login"  element={<PortalSignIn kind="introducer" />} />
          <Route path="/partner/login"     element={<PortalSignIn kind="campaign" />} />
          <Route path="/lender/apply"      element={<PortalApply  kind="lender" />} />
          <Route path="/introducer/apply"  element={<PortalApply  kind="introducer" />} />
          <Route path="/partners/lender"     element={<PortalSignIn kind="lender" />} />
          <Route path="/partners/introducer" element={<PortalSignIn kind="introducer" />} />

          {/* Lender desk — invite-only, a CRM workspace with a page per section */}
          <Route path="/lender" element={<ProtectedRoute roles={["lender", "admin"]}><LenderPortal /></ProtectedRoute>}>
            <Route index element={<LenderCases />} />
            <Route path="cases/:id" element={<LenderCaseRecord />} />
            <Route path="criteria" element={<LenderCriteria />} />
            <Route path="crm" element={<LenderCrm />} />
            <Route path="fees" element={<LenderFees />} />
            <Route path="documents" element={<LenderDocuments />} />
            <Route path="settings" element={<LenderSettings />} />
            <Route path="messages" element={<PortalMessages />} />
            <Route path="connie" element={<AskConnie />} />
            <Route path="feedback" element={<HelpShape />} />
          </Route>
          <Route path="/launch" element={<PortalLaunch />} />

          {/* Introducer portal — invite-only. /refer is the legacy path, /introducer is canonical. */}
          <Route path="/refer/*" element={<Navigate to="/introducer" replace />} />
          <Route path="/introducer" element={<ProtectedRoute roles={["referrer", "admin"]}><IntroducerPortal /></ProtectedRoute>}>
            <Route index element={<IntroducerHome />} />
            <Route path="cases" element={<IntroducerWorkspace />} />
            <Route path="submit" element={<IntroducerSubmit />} />
            <Route path="cases/:id" element={<IntroducerCaseRecord />} />
            {/* Commission was a second copy of Earnings and of Your network. Old links and
                emails still land, on the page that now carries the whole story. */}
            <Route path="commission" element={<Navigate to="/introducer/performance" replace />} />
            <Route path="statements" element={<IntroducerStatements />} />
            <Route path="performance" element={<IntroducerPerformance />} />
            <Route path="media-kit" element={<IntroducerMediaKit />} />
            <Route path="channels" element={<IntroducerChannels />} />
            <Route path="api" element={<IntroducerApi />} />
            <Route path="network" element={<IntroducerNetwork />} />
            <Route path="paperwork" element={<IntroducerPaperwork />} />
            <Route path="account" element={<IntroducerAccount />} />
            <Route path="messages" element={<PortalMessages />} />
            <Route path="connie" element={<AskConnie />} />
            <Route path="feedback" element={<HelpShape />} />
          </Route>

          {/* The floor board — a screen on the wall, so it sits outside the portal chrome */}
          <Route path="/introducer/board" element={<ProtectedRoute roles={["referrer", "admin"]}><IntroducerBoard /></ProtectedRoute>} />

          {/* Invite claim — public, token-gated */}
          <Route path="/hub-invite/:token" element={<HubInvite />} />
          <Route path="/apply/status" element={<ApplyStatus />} />
          <Route path="/apply/status/:token" element={<ApplyStatus />} />
          <Route path="/apply/docs/:token" element={<ClientDocUpload />} />
          <Route path="/apply/question/:token" element={<ClientAnswer />} />
          <Route path="/apply/case/:token" element={<ClientCaseChase />} />
          <Route path="/apply/i/:token" element={<PublicApply />} />
          <Route path="/apply/:slug" element={<PublicApply />} />
          {/* The link a media partner puts in an ad: logs the tap, keeps the click id, then hands over */}
          <Route path="/c/:code" element={<CampaignRedirect />} />

          {/* Publisher */}
          <Route element={<ProtectedRoute roles={["publisher", "admin"]}><PublisherLayout /></ProtectedRoute>}>
            <Route path="/publisher" element={<PublisherOverview />} />
            <Route path="/publisher/leads" element={<PublisherLeads />} />
            <Route path="/publisher/sectors" element={<PublisherSectors />} />
            <Route path="/publisher/earnings" element={<PublisherEarnings />} />
            <Route path="/publisher/payouts" element={<PublisherPayouts />} />
            <Route path="/publisher/api-keys" element={<PublisherApiKeys />} />
            <Route path="/publisher/landers" element={<PublisherLanders />} />
            <Route path="/publisher/landers/:id" element={<PublisherLanderEdit />} />
            <Route path="/publisher/contracts" element={<PublisherContracts />} />
            <Route path="/publisher/leaderboard" element={<PublisherLeaderboard />} />
            <Route path="/publisher/support" element={<PublisherSupport />} />
          </Route>
          <Route path="/publisher/settings" element={<ProtectedRoute roles={["publisher", "admin"]}><StandaloneSettings /></ProtectedRoute>} />

          {/* Affiliate */}
          <Route path="/affiliate" element={<ProtectedRoute roles={["affiliate", "admin"]}><DashboardLayout role="affiliate" /></ProtectedRoute>}>
            <Route index element={<AffiliateOverview />} />
            <Route path="links" element={<AffiliateLinks />} />
            <Route path="landers" element={<AffiliateLanders />} />
            <Route path="analytics" element={<AffiliateAnalytics />} />
            <Route path="commissions" element={<AffiliateCommissions />} />
            <Route path="creatives" element={<AffiliateCreatives />} />
            <Route path="contracts" element={<AffiliateContracts />} />
            <Route path="my-feedback" element={<MyFeedback />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Public lander (no auth) — hosted at /l/:slug */}
          <Route path="/l/:slug" element={<PublicLander />} />

          <Route path="*" element={<Wrap><div className="mx-auto max-w-3xl px-6 py-32 text-center"><h1 className="text-4xl font-semibold">Page not found</h1><p className="text-slate-600 mt-3">The page you are looking for does not exist.</p></div></Wrap>} />
        </Routes>
        <Toaster position="top-right" />
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
  );
}
